/**
 * Magento 2 Integration
 * Provides integration with Magento 2 e-commerce platform using REST and GraphQL APIs
 */

import { BaseEcommerceIntegration } from '../base/BaseEcommerceIntegration';
import {
  EcommerceProduct,
  EcommerceCustomer,
  EcommerceOrder,
  RecommendationContext,
  ProductImage,
  ProductVariant,
  CustomerAddress,
  OrderLineItem
} from '../base/BaseEcommerceIntegration';
import { IntegrationConfig, ConnectionResult, TestResult, ValidationResult } from '../../../types/integration';

// Magento-specific types
export interface MagentoConfig {
  baseUrl: string;
  adminToken: string;
  storeCode?: string;
  websiteId?: number;
  storeId?: number;
  version: '2.3' | '2.4';
  enableGraphQL: boolean;
  webhookSecret?: string;
}

export interface MagentoProduct {
  id: number;
  sku: string;
  name: string;
  attribute_set_id: number;
  price: number;
  status: number; // 1 = enabled, 2 = disabled
  visibility: number; // 1 = not visible, 2 = catalog, 3 = search, 4 = catalog+search
  type_id: string; // simple, configurable, grouped, virtual, bundle, downloadable
  created_at: string;
  updated_at: string;
  weight?: number;
  extension_attributes?: {
    website_ids?: number[];
    category_links?: MagentoCategoryLink[];
    stock_item?: MagentoStockItem;
    configurable_product_options?: MagentoConfigurableOption[];
    configurable_product_links?: number[];
  };
  product_links?: MagentoProductLink[];
  options?: MagentoCustomOption[];
  media_gallery_entries?: MagentoMediaGalleryEntry[];
  tier_prices?: MagentoTierPrice[];
  custom_attributes?: MagentoCustomAttribute[];
}

export interface MagentoCategoryLink {
  position: number;
  category_id: string;
}

export interface MagentoStockItem {
  item_id: number;
  product_id: number;
  stock_id: number;
  qty: number;
  is_in_stock: boolean;
  is_qty_decimal: boolean;
  show_default_notification_message: boolean;
  use_config_min_qty: boolean;
  min_qty: number;
  use_config_min_sale_qty: number;
  min_sale_qty: number;
  use_config_max_sale_qty: boolean;
  max_sale_qty: number;
  use_config_backorders: boolean;
  backorders: number;
  use_config_notify_stock_qty: boolean;
  notify_stock_qty: number;
  use_config_qty_increments: boolean;
  qty_increments: number;
  use_config_enable_qty_inc: boolean;
  enable_qty_increments: boolean;
  use_config_manage_stock: boolean;
  manage_stock: boolean;
  low_stock_date?: string;
  is_decimal_divided: boolean;
  stock_status_changed_auto: number;
}

export interface MagentoConfigurableOption {
  id: number;
  attribute_id: string;
  label: string;
  position: number;
  is_use_default: boolean;
  values: MagentoConfigurableOptionValue[];
}

export interface MagentoConfigurableOptionValue {
  value_index: number;
}

export interface MagentoProductLink {
  sku: string;
  link_type: string; // related, upsell, crosssell
  linked_product_sku: string;
  linked_product_type: string;
  position: number;
}

export interface MagentoCustomOption {
  product_sku: string;
  option_id: number;
  title: string;
  type: string;
  sort_order: number;
  is_require: boolean;
  price?: number;
  price_type?: string;
  sku?: string;
  file_extension?: string;
  max_characters?: number;
  image_size_x?: number;
  image_size_y?: number;
  values?: MagentoCustomOptionValue[];
}

export interface MagentoCustomOptionValue {
  title: string;
  sort_order: number;
  price: number;
  price_type: string;
  sku?: string;
  option_type_id?: number;
}

export interface MagentoMediaGalleryEntry {
  id: number;
  media_type: string;
  label?: string;
  position: number;
  disabled: boolean;
  types: string[];
  file: string;
}

export interface MagentoTierPrice {
  customer_group_id: number;
  qty: number;
  value: number;
}

export interface MagentoCustomAttribute {
  attribute_code: string;
  value: any;
}

export interface MagentoCustomer {
  id: number;
  group_id: number;
  default_billing?: string;
  default_shipping?: string;
  confirmation?: string;
  created_at: string;
  updated_at: string;
  created_in?: string;
  dob?: string;
  email: string;
  firstname: string;
  lastname: string;
  middlename?: string;
  prefix?: string;
  suffix?: string;
  gender?: number;
  store_id: number;
  taxvat?: string;
  website_id: number;
  addresses?: MagentoAddress[];
  disable_auto_group_change: number;
  extension_attributes?: {
    is_subscribed?: boolean;
  };
  custom_attributes?: MagentoCustomAttribute[];
}

export interface MagentoAddress {
  id?: number;
  customer_id?: number;
  region?: {
    region_code?: string;
    region?: string;
    region_id?: number;
  };
  region_id?: number;
  country_id: string;
  street: string[];
  company?: string;
  telephone?: string;
  fax?: string;
  postcode?: string;
  city?: string;
  firstname?: string;
  lastname?: string;
  middlename?: string;
  prefix?: string;
  suffix?: string;
  vat_id?: string;
  default_shipping?: boolean;
  default_billing?: boolean;
  extension_attributes?: any;
  custom_attributes?: MagentoCustomAttribute[];
}

export interface MagentoOrder {
  adjustment_negative?: number;
  adjustment_positive?: number;
  applied_rule_ids?: string;
  base_adjustment_negative?: number;
  base_adjustment_positive?: number;
  base_currency_code?: string;
  base_discount_amount?: number;
  base_discount_canceled?: number;
  base_discount_invoiced?: number;
  base_discount_refunded?: number;
  base_grand_total: number;
  base_discount_tax_compensation_amount?: number;
  base_discount_tax_compensation_invoiced?: number;
  base_discount_tax_compensation_refunded?: number;
  base_shipping_amount?: number;
  base_shipping_canceled?: number;
  base_shipping_discount_amount?: number;
  base_shipping_discount_tax_compensation_amnt?: number;
  base_shipping_incl_tax?: number;
  base_shipping_invoiced?: number;
  base_shipping_refunded?: number;
  base_shipping_tax_amount?: number;
  base_shipping_tax_refunded?: number;
  base_subtotal?: number;
  base_subtotal_canceled?: number;
  base_subtotal_incl_tax?: number;
  base_subtotal_invoiced?: number;
  base_subtotal_refunded?: number;
  base_tax_amount?: number;
  base_tax_canceled?: number;
  base_tax_invoiced?: number;
  base_tax_refunded?: number;
  base_total_canceled?: number;
  base_total_due?: number;
  base_total_invoiced?: number;
  base_total_invoiced_cost?: number;
  base_total_offline_refunded?: number;
  base_total_online_refunded?: number;
  base_total_paid?: number;
  base_total_qty_ordered?: number;
  base_total_refunded?: number;
  base_to_global_rate?: number;
  base_to_order_rate?: number;
  billing_address_id?: number;
  can_ship_partially?: number;
  can_ship_partially_item?: number;
  coupon_code?: string;
  created_at: string;
  customer_dob?: string;
  customer_email: string;
  customer_firstname?: string;
  customer_gender?: number;
  customer_group_id?: number;
  customer_id?: number;
  customer_is_guest?: number;
  customer_lastname?: string;
  customer_middlename?: string;
  customer_note?: string;
  customer_note_notify?: number;
  customer_prefix?: string;
  customer_suffix?: string;
  customer_taxvat?: string;
  discount_amount?: number;
  discount_canceled?: number;
  discount_description?: string;
  discount_invoiced?: number;
  discount_refunded?: number;
  edit_increment?: number;
  email_sent?: number;
  entity_id: number;
  ext_customer_id?: string;
  ext_order_id?: string;
  forced_shipment_with_invoice?: number;
  global_currency_code?: string;
  grand_total: number;
  discount_tax_compensation_amount?: number;
  discount_tax_compensation_invoiced?: number;
  discount_tax_compensation_refunded?: number;
  hold_before_state?: string;
  hold_before_status?: string;
  increment_id: string;
  is_virtual?: number;
  order_currency_code?: string;
  original_increment_id?: string;
  payment_authorization_amount?: number;
  payment_auth_expiration?: number;
  protect_code?: string;
  quote_address_id?: number;
  quote_id?: number;
  relation_child_id?: string;
  relation_child_real_id?: string;
  relation_parent_id?: string;
  relation_parent_real_id?: string;
  remote_ip?: string;
  shipping_amount?: number;
  shipping_canceled?: number;
  shipping_description?: string;
  shipping_discount_amount?: number;
  shipping_discount_tax_compensation_amount?: number;
  shipping_incl_tax?: number;
  shipping_invoiced?: number;
  shipping_refunded?: number;
  shipping_tax_amount?: number;
  shipping_tax_refunded?: number;
  state: string;
  status: string;
  store_currency_code?: string;
  store_id?: number;
  store_name?: string;
  store_to_base_rate?: number;
  store_to_order_rate?: number;
  subtotal?: number;
  subtotal_canceled?: number;
  subtotal_incl_tax?: number;
  subtotal_invoiced?: number;
  subtotal_refunded?: number;
  tax_amount?: number;
  tax_canceled?: number;
  tax_invoiced?: number;
  tax_refunded?: number;
  total_canceled?: number;
  total_due?: number;
  total_invoiced?: number;
  total_item_count?: number;
  total_offline_refunded?: number;
  total_online_refunded?: number;
  total_paid?: number;
  total_qty_ordered?: number;
  total_refunded?: number;
  updated_at: string;
  weight?: number;
  x_forwarded_for?: string;
  items?: MagentoOrderItem[];
  billing_address?: MagentoAddress;
  payment?: MagentoPayment;
  status_histories?: MagentoOrderStatusHistory[];
  extension_attributes?: {
    shipping_assignments?: MagentoShippingAssignment[];
    payment_additional_info?: MagentoPaymentAdditionalInfo[];
    applied_taxes?: MagentoAppliedTax[];
    item_applied_taxes?: MagentoItemAppliedTax[];
    converting_from_quote?: boolean;
  };
}

export interface MagentoOrderItem {
  amount_refunded?: number;
  applied_rule_ids?: string;
  base_amount_refunded?: number;
  base_cost?: number;
  base_discount_amount?: number;
  base_discount_invoiced?: number;
  base_discount_refunded?: number;
  base_discount_tax_compensation_amount?: number;
  base_discount_tax_compensation_invoiced?: number;
  base_discount_tax_compensation_refunded?: number;
  base_original_price?: number;
  base_price: number;
  base_price_incl_tax?: number;
  base_row_invoiced?: number;
  base_row_total: number;
  base_row_total_incl_tax?: number;
  base_tax_amount?: number;
  base_tax_before_discount?: number;
  base_tax_invoiced?: number;
  base_tax_refunded?: number;
  base_weee_tax_applied_amount?: number;
  base_weee_tax_applied_row_amnt?: number;
  base_weee_tax_disposition?: number;
  base_weee_tax_row_disposition?: number;
  created_at: string;
  description?: string;
  discount_amount?: number;
  discount_invoiced?: number;
  discount_percent?: number;
  discount_refunded?: number;
  event_id?: number;
  ext_order_item_id?: string;
  free_shipping?: number;
  gw_base_price?: number;
  gw_base_price_invoiced?: number;
  gw_base_price_refunded?: number;
  gw_base_tax_amount?: number;
  gw_base_tax_amount_invoiced?: number;
  gw_base_tax_amount_refunded?: number;
  gw_id?: number;
  gw_price?: number;
  gw_price_invoiced?: number;
  gw_price_refunded?: number;
  gw_tax_amount?: number;
  gw_tax_amount_invoiced?: number;
  gw_tax_amount_refunded?: number;
  discount_tax_compensation_amount?: number;
  discount_tax_compensation_canceled?: number;
  discount_tax_compensation_invoiced?: number;
  discount_tax_compensation_refunded?: number;
  is_qty_decimal?: number;
  is_virtual?: number;
  item_id: number;
  locked_do_invoice?: number;
  locked_do_ship?: number;
  name: string;
  no_discount?: number;
  order_id: number;
  original_price?: number;
  parent_item_id?: number;
  price: number;
  price_incl_tax?: number;
  product_id?: number;
  product_type?: string;
  qty_backordered?: number;
  qty_canceled?: number;
  qty_invoiced?: number;
  qty_ordered?: number;
  qty_refunded?: number;
  qty_returned?: number;
  qty_shipped?: number;
  quote_item_id?: number;
  row_invoiced?: number;
  row_total: number;
  row_total_incl_tax?: number;
  row_weight?: number;
  sku: string;
  store_id?: number;
  tax_amount?: number;
  tax_before_discount?: number;
  tax_canceled?: number;
  tax_invoiced?: number;
  tax_percent?: number;
  tax_refunded?: number;
  updated_at: string;
  weee_tax_applied?: string;
  weee_tax_applied_amount?: number;
  weee_tax_applied_row_amount?: number;
  weee_tax_disposition?: number;
  weee_tax_row_disposition?: number;
  weight?: number;
  parent_item?: MagentoOrderItem;
  product_option?: {
    extension_attributes?: {
      custom_options?: MagentoCustomOptionValue[];
      bundle_options?: MagentoBundleOption[];
      configurable_item_options?: MagentoConfigurableItemOption[];
    };
  };
}

export interface MagentoPayment {
  account_status?: string;
  additional_data?: string;
  additional_information?: string[];
  address_status?: string;
  amount_authorized?: number;
  amount_canceled?: number;
  amount_ordered?: number;
  amount_paid?: number;
  amount_refunded?: number;
  anet_trans_method?: string;
  base_amount_authorized?: number;
  base_amount_canceled?: number;
  base_amount_ordered?: number;
  base_amount_paid?: number;
  base_amount_paid_online?: number;
  base_amount_refunded?: number;
  base_amount_refunded_online?: number;
  base_shipping_amount?: number;
  base_shipping_captured?: number;
  base_shipping_refunded?: number;
  cc_approval?: string;
  cc_avs_status?: string;
  cc_cid_status?: string;
  cc_debug_request_body?: string;
  cc_debug_response_body?: string;
  cc_debug_response_serialized?: string;
  cc_exp_month?: string;
  cc_exp_year?: string;
  cc_last4?: string;
  cc_number_enc?: string;
  cc_owner?: string;
  cc_secure_verify?: string;
  cc_ss_issue?: string;
  cc_ss_start_month?: string;
  cc_ss_start_year?: string;
  cc_status?: string;
  cc_status_description?: string;
  cc_trans_id?: string;
  cc_type?: string;
  echeck_account_name?: string;
  echeck_account_type?: string;
  echeck_bank_name?: string;
  echeck_routing_number?: string;
  echeck_type?: string;
  entity_id: number;
  last_trans_id?: string;
  method: string;
  parent_id: number;
  po_number?: string;
  protection_eligibility?: string;
  quote_payment_id?: number;
  shipping_amount?: number;
  shipping_captured?: number;
  shipping_refunded?: number;
}

export interface MagentoOrderStatusHistory {
  comment: string;
  created_at: string;
  entity_id: number;
  entity_name?: string;
  is_customer_notified: number;
  is_visible_on_front: number;
  parent_id: number;
  status: string;
}

export interface MagentoShippingAssignment {
  shipping: MagentoShipping;
  items: MagentoOrderItem[];
  stock_id?: number;
}

export interface MagentoShipping {
  address: MagentoAddress;
  method: string;
}

export interface MagentoPaymentAdditionalInfo {
  key: string;
  value: string;
}

export interface MagentoAppliedTax {
  code: string;
  title: string;
  percent: number;
  amount: number;
  base_amount: number;
}

export interface MagentoItemAppliedTax {
  type: string;
  item_id: number;
  associated_item_id?: number;
  applied_taxes: MagentoAppliedTax[];
}

export interface MagentoBundleOption {
  option_id: number;
  option_qty: number;
  option_selections: number[];
}

export interface MagentoConfigurableItemOption {
  option_id: string;
  option_value: number;
}

export class MagentoIntegration extends BaseEcommerceIntegration {
  private platformConfig: MagentoConfig;
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(integrationConfig: IntegrationConfig) {
    super(integrationConfig);
    this.platformConfig = integrationConfig.settings.customSettings as MagentoConfig;
    this.baseUrl = `${this.platformConfig.baseUrl}/rest/${this.platformConfig.storeCode || 'default'}/V1`;
    this.headers = {
      'Authorization': `Bearer ${this.platformConfig.adminToken}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  async connect(): Promise<ConnectionResult> {
    try {
      // Test connection by getting store information
      const response = await this.makeRequest('GET', '/store/storeConfigs');
      if (!response.ok) {
        const error = `Failed to connect to Magento: ${response.status} ${response.statusText}`;
        await this.emitEvent('connection.failed', { 
          platform: 'magento', 
          error 
        });
        return {
          success: false,
          message: error
        };
      }
      
      await this.emitEvent('connection.established', { platform: 'magento' });
      return {
        success: true,
        message: 'Successfully connected to Magento store'
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await this.emitEvent('connection.failed', { 
        platform: 'magento', 
        error: errorMessage 
      });
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  async disconnect(): Promise<void> {
    // Magento doesn't require explicit disconnection for token-based auth
    await this.emitEvent('connection.closed', { platform: 'magento' });
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const response = await this.makeRequest('GET', '/store/storeConfigs');
      const duration = Date.now() - startTime;
      
      if (response.ok) {
        return {
          success: true,
          message: 'Connection test successful',
          duration
        };
      } else {
        return {
          success: false,
          message: `Connection test failed: ${response.status} ${response.statusText}`,
          duration
        };
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed',
        duration
      };
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; suggestion?: string }> = [];

    // Validate base URL
    if (!this.platformConfig.baseUrl) {
      errors.push({ field: 'baseUrl', message: 'Base URL is required', code: 'REQUIRED' });
    } else {
      try {
        new URL(this.platformConfig.baseUrl);
      } catch {
        errors.push({ field: 'baseUrl', message: 'Base URL must be a valid URL', code: 'INVALID_FORMAT' });
      }
    }

    // Validate admin token
    if (!this.platformConfig.adminToken) {
      errors.push({ field: 'adminToken', message: 'Admin token is required', code: 'REQUIRED' });
    } else if (this.platformConfig.adminToken.length < 10) {
      errors.push({ field: 'adminToken', message: 'Admin token appears to be invalid (too short)', code: 'INVALID_LENGTH' });
    }

    // Validate store code format
    if (this.platformConfig.storeCode && !/^[a-z0-9_]+$/.test(this.platformConfig.storeCode)) {
      errors.push({ field: 'storeCode', message: 'Store code must contain only lowercase letters, numbers, and underscores', code: 'INVALID_FORMAT' });
    }

    // Validate website and store IDs
    if (this.platformConfig.websiteId !== undefined && (this.platformConfig.websiteId < 0 || !Number.isInteger(this.platformConfig.websiteId))) {
      errors.push({ field: 'websiteId', message: 'Website ID must be a non-negative integer', code: 'INVALID_VALUE' });
    }

    if (this.platformConfig.storeId !== undefined && (this.platformConfig.storeId < 0 || !Number.isInteger(this.platformConfig.storeId))) {
      errors.push({ field: 'storeId', message: 'Store ID must be a non-negative integer', code: 'INVALID_VALUE' });
    }

    // Validate version
    if (this.platformConfig.version && !['2.3', '2.4'].includes(this.platformConfig.version)) {
      errors.push({ field: 'version', message: 'Version must be either 2.3 or 2.4', code: 'INVALID_VALUE' });
    }

    // Validate webhook secret if provided
    if (this.platformConfig.webhookSecret && this.platformConfig.webhookSecret.length < 16) {
      errors.push({ field: 'webhookSecret', message: 'Webhook secret must be at least 16 characters long', code: 'INVALID_LENGTH' });
    }

    // Add warnings for optional configurations
    if (!this.platformConfig.webhookSecret) {
      warnings.push({ 
        field: 'webhookSecret', 
        message: 'Webhook secret not provided - real-time sync will be disabled',
        suggestion: 'Add a webhook secret to enable real-time synchronization'
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async syncProducts(): Promise<EcommerceProduct[]> {
    try {
      const products: EcommerceProduct[] = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const searchCriteria = {
          searchCriteria: {
            pageSize,
            currentPage,
            filterGroups: [
              {
                filters: [
                  {
                    field: 'status',
                    value: '1', // Only enabled products
                    conditionType: 'eq'
                  }
                ]
              }
            ]
          }
        };

        const queryString = this.buildSearchCriteriaQuery(searchCriteria.searchCriteria);
        const response = await this.makeRequest('GET', `/products?${queryString}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }

        const data = await response.json();
        const magentoProducts: MagentoProduct[] = data.items || [];

        for (const magentoProduct of magentoProducts) {
          const product = await this.convertMagentoProductToEcommerce(magentoProduct);
          products.push(product);
        }

        hasMorePages = data.items && data.items.length === pageSize;
        currentPage++;
      }

      await this.emitEvent('products.synced', { count: products.length });
      return products;
    } catch (error) {
      await this.emitEvent('products.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async syncCustomers(): Promise<EcommerceCustomer[]> {
    try {
      const customers: EcommerceCustomer[] = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const searchCriteria = {
          searchCriteria: {
            pageSize,
            currentPage
          }
        };

        const queryString = this.buildSearchCriteriaQuery(searchCriteria.searchCriteria);
        const response = await this.makeRequest('GET', `/customers/search?${queryString}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch customers: ${response.status}`);
        }

        const data = await response.json();
        const magentoCustomers: MagentoCustomer[] = data.items || [];

        for (const magentoCustomer of magentoCustomers) {
          const customer = await this.convertMagentoCustomerToEcommerce(magentoCustomer);
          customers.push(customer);
        }

        hasMorePages = data.items && data.items.length === pageSize;
        currentPage++;
      }

      await this.emitEvent('customers.synced', { count: customers.length });
      return customers;
    } catch (error) {
      await this.emitEvent('customers.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async syncOrders(dateRange?: { start: Date; end: Date }): Promise<EcommerceOrder[]> {
    try {
      const orders: EcommerceOrder[] = [];
      let currentPage = 1;
      const pageSize = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const searchCriteria: any = {
          searchCriteria: {
            pageSize,
            currentPage,
            filterGroups: []
          }
        };

        // Add date range filter if provided
        if (dateRange) {
          searchCriteria.searchCriteria.filterGroups.push({
            filters: [
              {
                field: 'created_at',
                value: dateRange.start.toISOString(),
                conditionType: 'gteq'
              },
              {
                field: 'created_at',
                value: dateRange.end.toISOString(),
                conditionType: 'lteq'
              }
            ]
          });
        }

        const queryString = this.buildSearchCriteriaQuery(searchCriteria.searchCriteria);
        const response = await this.makeRequest('GET', `/orders?${queryString}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.status}`);
        }

        const data = await response.json();
        const magentoOrders: MagentoOrder[] = data.items || [];

        for (const magentoOrder of magentoOrders) {
          const order = await this.convertMagentoOrderToEcommerce(magentoOrder);
          orders.push(order);
        }

        hasMorePages = data.items && data.items.length === pageSize;
        currentPage++;
      }

      await this.emitEvent('orders.synced', { count: orders.length, dateRange });
      return orders;
    } catch (error) {
      await this.emitEvent('orders.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        dateRange 
      });
      throw error;
    }
  }

  async getProduct(productId: string): Promise<EcommerceProduct | null> {
    try {
      const response = await this.makeRequest('GET', `/products/${encodeURIComponent(productId)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const magentoProduct: MagentoProduct = await response.json();
      return await this.convertMagentoProductToEcommerce(magentoProduct);
    } catch (error) {
      await this.emitEvent('product.fetch_failed', { 
        productId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getCustomer(customerId: string): Promise<EcommerceCustomer | null> {
    try {
      const response = await this.makeRequest('GET', `/customers/${customerId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch customer: ${response.status}`);
      }

      const magentoCustomer: MagentoCustomer = await response.json();
      return await this.convertMagentoCustomerToEcommerce(magentoCustomer);
    } catch (error) {
      await this.emitEvent('customer.fetch_failed', { 
        customerId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getOrder(orderId: string): Promise<EcommerceOrder | null> {
    try {
      const response = await this.makeRequest('GET', `/orders/${orderId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch order: ${response.status}`);
      }

      const magentoOrder: MagentoOrder = await response.json();
      return await this.convertMagentoOrderToEcommerce(magentoOrder);
    } catch (error) {
      await this.emitEvent('order.fetch_failed', { 
        orderId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async updateInventory(updates: { productId: string; variantId?: string; inventory: number }[]): Promise<void> {
    try {
      for (const update of updates) {
        const stockItem = {
          stockItem: {
            qty: update.inventory,
            is_in_stock: update.inventory > 0
          }
        };

        const response = await this.makeRequest(
          'PUT', 
          `/products/${encodeURIComponent(update.productId)}/stockItems/1`,
          stockItem
        );

        if (!response.ok) {
          throw new Error(`Failed to update inventory for product ${update.productId}: ${response.status}`);
        }

        await this.handleInventoryChange(update.productId, update.variantId, update.inventory);
      }

      await this.emitEvent('inventory.updated', { updates });
    } catch (error) {
      await this.emitEvent('inventory.update_failed', { 
        updates, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getRecommendations(context: RecommendationContext): Promise<EcommerceProduct[]> {
    try {
      // Magento doesn't have built-in recommendation API, so we'll implement basic logic
      const recommendations: EcommerceProduct[] = [];

      if (context.type === 'related' && context.currentProductId) {
        // Get related products
        const product = await this.getProduct(context.currentProductId);
        if (product) {
          // This would need to be implemented based on Magento's product relations
          // For now, return empty array
        }
      } else if (context.type === 'trending') {
        // Get trending products based on sales
        const searchCriteria = {
          searchCriteria: {
            pageSize: context.limit,
            currentPage: 1,
            sortOrders: [
              {
                field: 'created_at',
                direction: 'DESC'
              }
            ]
          }
        };

        const queryString = this.buildSearchCriteriaQuery(searchCriteria.searchCriteria);
        const response = await this.makeRequest('GET', `/products?${queryString}`);
        
        if (response.ok) {
          const data = await response.json();
          const magentoProducts: MagentoProduct[] = data.items || [];

          for (const magentoProduct of magentoProducts.slice(0, context.limit)) {
            const product = await this.convertMagentoProductToEcommerce(magentoProduct);
            recommendations.push(product);
          }
        }
      }

      await this.emitEvent('recommendations.generated', { 
        context, 
        count: recommendations.length 
      });
      
      return recommendations;
    } catch (error) {
      await this.emitEvent('recommendations.generation_failed', { 
        context, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Helper methods

  private async makeRequest(method: string, endpoint: string, body?: any): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: this.headers
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return fetch(url, options);
  }

  private buildSearchCriteriaQuery(searchCriteria: any): string {
    const params = new URLSearchParams();
    
    if (searchCriteria.pageSize) {
      params.append('searchCriteria[pageSize]', searchCriteria.pageSize.toString());
    }
    
    if (searchCriteria.currentPage) {
      params.append('searchCriteria[currentPage]', searchCriteria.currentPage.toString());
    }

    if (searchCriteria.filterGroups) {
      searchCriteria.filterGroups.forEach((group: any, groupIndex: number) => {
        if (group.filters) {
          group.filters.forEach((filter: any, filterIndex: number) => {
            params.append(`searchCriteria[filterGroups][${groupIndex}][filters][${filterIndex}][field]`, filter.field);
            params.append(`searchCriteria[filterGroups][${groupIndex}][filters][${filterIndex}][value]`, filter.value);
            params.append(`searchCriteria[filterGroups][${groupIndex}][filters][${filterIndex}][conditionType]`, filter.conditionType);
          });
        }
      });
    }

    if (searchCriteria.sortOrders) {
      searchCriteria.sortOrders.forEach((sort: any, index: number) => {
        params.append(`searchCriteria[sortOrders][${index}][field]`, sort.field);
        params.append(`searchCriteria[sortOrders][${index}][direction]`, sort.direction);
      });
    }

    return params.toString();
  }

  private async convertMagentoProductToEcommerce(magentoProduct: MagentoProduct): Promise<EcommerceProduct> {
    const images: ProductImage[] = [];
    
    if (magentoProduct.media_gallery_entries) {
      magentoProduct.media_gallery_entries.forEach((entry, index) => {
        images.push({
          id: entry.id.toString(),
          url: `${this.platformConfig.baseUrl}/media/catalog/product${entry.file}`,
          altText: entry.label || magentoProduct.name,
          position: entry.position || index
        });
      });
    }

    const variants: ProductVariant[] = [];
    
    // Handle configurable products
    if (magentoProduct.type_id === 'configurable' && 
        magentoProduct.extension_attributes?.configurable_product_links) {
      
      for (const childId of magentoProduct.extension_attributes.configurable_product_links) {
        try {
          const childProduct = await this.getProduct(childId.toString());
          if (childProduct) {
            variants.push({
              id: childProduct.id,
              title: childProduct.title,
              price: childProduct.price,
              sku: childProduct.sku || '',
              inventory: childProduct.inventory,
              options: {} // Would need to map configurable options
            });
          }
        } catch (error) {
          // Skip failed child products
          console.warn(`Failed to fetch child product ${childId}:`, error);
        }
      }
    }

    // Get categories
    const categories: string[] = [];
    if (magentoProduct.extension_attributes?.category_links) {
      categories.push(...magentoProduct.extension_attributes.category_links.map(link => link.category_id));
    }

    // Get custom attributes
    const customFields: Record<string, any> = {};
    if (magentoProduct.custom_attributes) {
      magentoProduct.custom_attributes.forEach(attr => {
        customFields[attr.attribute_code] = attr.value;
      });
    }

    return {
      id: magentoProduct.id.toString(),
      title: magentoProduct.name,
      description: customFields.description || '',
      price: magentoProduct.price,
      compareAtPrice: customFields.msrp || undefined,
      currency: 'USD', // Would need to get from store config
      sku: magentoProduct.sku,
      inventory: magentoProduct.extension_attributes?.stock_item?.qty || 0,
      images,
      categories,
      tags: [], // Magento doesn't have built-in tags like Shopify
      variants,
      status: magentoProduct.status === 1 ? 'active' : 'draft',
      createdAt: new Date(magentoProduct.created_at),
      updatedAt: new Date(magentoProduct.updated_at),
      customFields
    };
  }

  private async convertMagentoCustomerToEcommerce(magentoCustomer: MagentoCustomer): Promise<EcommerceCustomer> {
    const addresses: CustomerAddress[] = [];
    
    if (magentoCustomer.addresses) {
      magentoCustomer.addresses.forEach((addr, index) => {
        addresses.push({
          id: addr.id?.toString() || index.toString(),
          firstName: addr.firstname,
          lastName: addr.lastname,
          company: addr.company,
          address1: addr.street?.[0] || '',
          address2: addr.street?.[1],
          city: addr.city || '',
          province: addr.region?.region,
          country: addr.country_id,
          zip: addr.postcode || '',
          phone: addr.telephone,
          isDefault: addr.default_billing || addr.default_shipping || false
        });
      });
    }

    // Get custom attributes
    const customFields: Record<string, any> = {};
    if (magentoCustomer.custom_attributes) {
      magentoCustomer.custom_attributes.forEach(attr => {
        customFields[attr.attribute_code] = attr.value;
      });
    }

    // Calculate customer metrics (would need additional API calls in real implementation)
    const totalSpent = 0; // Would calculate from orders
    const orderCount = 0; // Would calculate from orders
    const averageOrderValue = orderCount > 0 ? totalSpent / orderCount : 0;

    return {
      id: magentoCustomer.id.toString(),
      email: magentoCustomer.email,
      firstName: magentoCustomer.firstname,
      lastName: magentoCustomer.lastname,
      phone: customFields.phone,
      addresses,
      totalSpent,
      orderCount,
      averageOrderValue,
      lastOrderAt: undefined, // Would get from orders
      tags: [], // Magento doesn't have built-in customer tags
      acceptsMarketing: magentoCustomer.extension_attributes?.is_subscribed || false,
      createdAt: new Date(magentoCustomer.created_at),
      updatedAt: new Date(magentoCustomer.updated_at),
      customFields
    };
  }

  private async convertMagentoOrderToEcommerce(magentoOrder: MagentoOrder): Promise<EcommerceOrder> {
    const lineItems: OrderLineItem[] = [];
    
    if (magentoOrder.items) {
      magentoOrder.items.forEach(item => {
        lineItems.push({
          id: item.item_id.toString(),
          productId: item.product_id?.toString() || '',
          variantId: undefined, // Magento handles variants differently
          title: item.name,
          quantity: item.qty_ordered || 0,
          price: item.price,
          totalPrice: item.row_total,
          sku: item.sku
        });
      });
    }

    // Convert billing address
    let billingAddress: CustomerAddress | undefined;
    if (magentoOrder.billing_address) {
      billingAddress = {
        id: magentoOrder.billing_address.id?.toString() || '0',
        firstName: magentoOrder.billing_address.firstname,
        lastName: magentoOrder.billing_address.lastname,
        company: magentoOrder.billing_address.company,
        address1: magentoOrder.billing_address.street?.[0] || '',
        address2: magentoOrder.billing_address.street?.[1],
        city: magentoOrder.billing_address.city || '',
        province: magentoOrder.billing_address.region?.region,
        country: magentoOrder.billing_address.country_id,
        zip: magentoOrder.billing_address.postcode || '',
        phone: magentoOrder.billing_address.telephone,
        isDefault: false
      };
    }

    // Convert shipping address (from shipping assignments)
    let shippingAddress: CustomerAddress | undefined;
    if (magentoOrder.extension_attributes?.shipping_assignments?.[0]?.shipping?.address) {
      const addr = magentoOrder.extension_attributes.shipping_assignments[0].shipping.address;
      shippingAddress = {
        id: addr.id?.toString() || '0',
        firstName: addr.firstname,
        lastName: addr.lastname,
        company: addr.company,
        address1: addr.street?.[0] || '',
        address2: addr.street?.[1],
        city: addr.city || '',
        province: addr.region?.region,
        country: addr.country_id,
        zip: addr.postcode || '',
        phone: addr.telephone,
        isDefault: false
      };
    }

    // Map Magento status to standard status
    let status: 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded' = 'pending';
    switch (magentoOrder.status) {
      case 'complete':
        status = 'fulfilled';
        break;
      case 'processing':
        status = 'paid';
        break;
      case 'canceled':
        status = 'cancelled';
        break;
      case 'closed':
        status = 'refunded';
        break;
      default:
        status = 'pending';
    }

    return {
      id: magentoOrder.entity_id.toString(),
      orderNumber: magentoOrder.increment_id,
      customerId: magentoOrder.customer_id?.toString() || '',
      email: magentoOrder.customer_email,
      totalPrice: magentoOrder.grand_total,
      subtotalPrice: magentoOrder.subtotal || 0,
      taxPrice: magentoOrder.tax_amount || 0,
      shippingPrice: magentoOrder.shipping_amount || 0,
      currency: magentoOrder.order_currency_code || 'USD',
      status,
      lineItems,
      shippingAddress,
      billingAddress,
      createdAt: new Date(magentoOrder.created_at),
      updatedAt: new Date(magentoOrder.updated_at),
      customFields: {
        magentoStatus: magentoOrder.status,
        magentoState: magentoOrder.state,
        storeId: magentoOrder.store_id,
        storeName: magentoOrder.store_name
      }
    };
  }
}